/**
 * The shape of a range slider's track, in noUiSlider's own terms (#414).
 *
 * noUiSlider takes a `range`: a list of (position on the track -> value) pairs,
 * with a step per segment. It draws the track and moves the thumbs; nothing
 * here moves anything. This file only describes the track:
 *
 * - **Linear** attributes get one even track.
 * - **Geometric** attributes (the registry's `scale`, REQ-DM-022) get a
 *   logarithmic track, so every equal ratio (every stop) has equal width and
 *   one extreme value cannot squash the rest (REQ-WEB-029). noUiSlider's
 *   segments are straight lines between their end points, so a log curve is a
 *   handful of end points that lie exactly on it; a segment spans a ratio of
 *   at most 1.33, where the straight line and the curve differ by well under a
 *   pixel on a phone.
 *
 * Steps: a thumb or an arrow key moves one step. A step is never finer than
 * the readout (`readStep`, the registry's display step, REQ-DM-016), so every
 * key press changes what the readout says. On a geometric track the step also
 * grows with magnitude (two significant figures: 0.84, 8.4, 84) because one
 * fixed step is wrong at one end or the other.
 *
 * Nothing here knows an attribute key (REQ-WEB-029).
 */

/** One entry of noUiSlider's `range`: [value] or [value, step]. */
export type RangeEntry = [number] | [number, number];
export type NoUiRange = { min: RangeEntry; max: RangeEntry } & Record<string, RangeEntry>;

/**
 * Where, inside each decade, a log track gets an end point. Every one is a
 * multiple of a tenth of its decade (so also of the step there), and the ratio
 * between neighbours never exceeds 1.34.
 */
const END_POINTS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8];

/** Decimal places a step needs: 0.01 -> 2, 1 -> 0. Capped, so float dust never counts. */
function placesOf(step: number): number {
  return Math.min(8, Math.max(0, Math.ceil(-Math.log10(step) - 1e-9)));
}

/** Round to a multiple of `step`, then to the decimals that multiple implies. */
function toMultiple(v: number, step: number): number {
  return Number((Math.round(v / step) * step).toFixed(placesOf(step)));
}

/** The decade a positive value is in: 8.4 -> 0, 84 -> 1, 0.84 -> -1. */
const decadeOf = (v: number) => Math.floor(Math.log10(v) + 1e-9);

/**
 * The step at a value: the readout's precision, or two significant figures if
 * that is coarser (geometric only).
 */
export function stepAt(v: number, geometric: boolean, readStep: number): number {
  if (!geometric || v <= 0) return readStep;
  const sig2 = Math.pow(10, decadeOf(v) - 1);
  return Math.max(readStep, Number(sig2.toFixed(placesOf(sig2))));
}

/** noUiSlider's `range` for these bounds. `min`/`max` are the observed span. */
export function buildRange(
  min: number,
  max: number,
  geometric: boolean,
  readStep: number,
): NoUiRange {
  const log = geometric && min > 0 && max > min;
  /** Where a value sits on the track, in percent. */
  const pos = (v: number) =>
    log ? (Math.log(v / min) / Math.log(max / min)) * 100 : ((v - min) / (max - min)) * 100;

  const stepMin = stepAt(min, log, readStep);
  const stepMax = stepAt(max, log, readStep);
  const onGrid = (v: number, step: number) => Math.abs(v - toMultiple(v, step)) < step * 1e-6;

  // The span's own ends are the data's minimum and maximum, and need not be on
  // the grid the readout can say (0.84 with 0.1 steps reads "0.8"). A grid
  // value that reads the same as an end would make a key press change nothing,
  // so each end gets one hop to the nearest grid value that reads differently;
  // every segment between starts on the grid.
  const first = onGrid(min, stepMin) ? null : toMultiple(min, stepMin) + stepMin;
  const last = onGrid(max, stepMax) ? null : toMultiple(max, stepMax) - stepMax;

  // End points strictly inside the span, each snapped to its own step.
  const inside = (v: number) =>
    v > min && v < max && pos(v) > 1e-4 && pos(v) < 100 - 1e-4 && (first === null || v >= first) && (last === null || v <= last);
  const found: number[] = [];
  if (log) {
    for (let k = decadeOf(min); k <= decadeOf(max); k++) {
      for (const m of END_POINTS) {
        const raw = Number((m * Math.pow(10, k)).toFixed(Math.max(0, -k + 1)));
        found.push(toMultiple(raw, stepAt(raw, true, readStep)));
      }
    }
  }
  if (first !== null) found.push(first);
  if (last !== null) found.push(last);
  const ends = [...new Set(found.filter(inside))].sort((a, b) => a - b);

  const range: NoUiRange = { min: [min], max: [max] };
  const hopFirst = first !== null && ends[0] === first;
  range.min = hopFirst ? [min, first - min] : [min, stepMin];
  for (const v of ends) {
    range[`${pos(v).toFixed(6)}%`] = [v, last !== null && v === last ? max - last : stepAt(v, log, readStep)];
  }
  return range;
}
